import { Alert, AlertDescription, AlertTitle, Card, CardContent, CardHeader, CardTitle, cn, Skeleton } from '@1trading/react';
import { CalendarClock, OctagonAlert, UserPlus } from 'lucide-react';
import { Link } from 'react-router-dom';
import { MetricTile } from '@/components/ui/metric-tile';
import { mmProgramPath } from '@/lib/paths';
import type { MmProgramSummary, UnconfiguredMarketMaker } from '@/services/api/mm-programs';
import { describeDaysFromToday } from '@/utils/date';
import { formatUsd } from '@/utils/format';
import { CHANGE_LABELS, type DashboardOverview, UPCOMING_WINDOW_DAYS, type UpcomingChange } from './dashboard';
import { UnconfiguredAccountList } from './set-up-market-maker';

export const OverviewTiles = ({ overview }: { overview: DashboardOverview }) => (
  <section aria-label="Program overview" className="grid grid-cols-2 gap-3 lg:grid-cols-[1.6fr_1fr_1fr_1fr]">
    <MetricTile className="col-span-2 lg:col-span-1" isHeadline label="Committed per month at full KPI" value={formatUsd(overview.committedMonthlyUsd, 0)} />
    <MetricTile hint={`of ${overview.totalMarketMakers} with settings`} label="Active market makers" value={String(overview.activeMarketMakers)} />
    <MetricTile label="Live instruments" value={String(overview.liveInstruments)} />
    <MetricTile className={overview.changesThisWeek ? 'border-amber-500/40' : undefined} label="Changes this week" value={String(overview.changesThisWeek)} />
  </section>
);
OverviewTiles.displayName = 'OverviewTiles';

export const OverviewTilesSkeleton = () => (
  <div aria-hidden className="grid grid-cols-2 gap-3 lg:grid-cols-[1.6fr_1fr_1fr_1fr]">
    <Skeleton className="col-span-2 h-28 lg:col-span-1" />
    <Skeleton className="h-28" />
    <Skeleton className="h-28" />
    <Skeleton className="h-28" />
  </div>
);
OverviewTilesSkeleton.displayName = 'OverviewTilesSkeleton';

/** The scoring job validates every document before it scores any, so one bad document stops all payouts. */
export const AttentionPanel = ({ marketMakers }: { marketMakers: MmProgramSummary[] }) => {
  if (!marketMakers.length) return null;
  return (
    <Alert variant="destructive">
      <OctagonAlert className="size-4" />
      <AlertTitle>Scoring is blocked until {marketMakers.length === 1 ? 'this market maker is' : `these ${marketMakers.length} market makers are`} fixed</AlertTitle>
      <AlertDescription>
        <ul className="flex flex-col gap-1.5">
          {marketMakers.map((marketMaker) => (
            <li className="flex flex-wrap items-baseline gap-x-2" key={marketMaker.p}>
              <Link className="font-medium underline underline-offset-2" to={mmProgramPath(marketMaker.p)}>
                {marketMaker.name || marketMaker.p}
              </Link>
              <span className="text-xs">{marketMaker.problems[0]}</span>
              {marketMaker.problems.length > 1 ? <span className="text-xs opacity-80">and {marketMaker.problems.length - 1} more</span> : null}
            </li>
          ))}
        </ul>
      </AlertDescription>
    </Alert>
  );
};
AttentionPanel.displayName = 'AttentionPanel';

const KIND_ACCENT: Record<UpcomingChange['kind'], string> = {
  starts: 'bg-emerald-500',
  ends: 'bg-muted-foreground',
  changes: 'bg-primary',
};

export const UpcomingChanges = ({ changes }: { changes: UpcomingChange[] }) => (
  <Card>
    <CardHeader className="pb-3">
      <CardTitle className="flex items-center gap-2 text-base">
        <CalendarClock className="size-4 text-muted-foreground" />
        Coming up
      </CardTitle>
    </CardHeader>
    <CardContent>
      {changes.length ? (
        <ol className="flex flex-col">
          {changes.map((change) => (
            <li className="relative border-l border-border py-2 pl-4" key={`${change.marketMaker.p}:${change.instrument}`}>
              <span aria-hidden className={cn('absolute top-3.5 -left-[4.5px] size-2 rounded-full ring-2 ring-card', KIND_ACCENT[change.kind])} />
              <div className="flex items-baseline justify-between gap-3">
                <span className="text-sm">
                  <span className="font-medium">{CHANGE_LABELS[change.kind]}</span> <span className="font-mono text-xs">{change.instrument}</span>
                </span>
                <span className="text-xs whitespace-nowrap text-muted-foreground tabular-nums">{change.date}</span>
              </div>
              <div className="flex items-baseline justify-between gap-3 text-xs text-muted-foreground">
                <Link className="hover:text-foreground hover:underline" to={mmProgramPath(change.marketMaker.p)}>
                  {change.marketMaker.name}
                  {change.marketMaker.active ? '' : ' (inactive)'}
                </Link>
                <span>{describeDaysFromToday(change.daysAway)}</span>
              </div>
            </li>
          ))}
        </ol>
      ) : (
        <p className="text-sm text-muted-foreground">No schedules start or end in the next {UPCOMING_WINDOW_DAYS} days.</p>
      )}
    </CardContent>
  </Card>
);
UpcomingChanges.displayName = 'UpcomingChanges';

interface UnconfiguredPanelProps {
  accounts: UnconfiguredMarketMaker[];
  accountsAvailable: boolean;
  canEdit: boolean;
  pendingPid: string | null;
  onSetUp: (pid: string) => void;
}

export const UnconfiguredPanel = ({ accounts, accountsAvailable, canEdit, pendingPid, onSetUp }: UnconfiguredPanelProps) => (
  <Card>
    <CardHeader className="pb-3">
      <CardTitle className="flex items-center gap-2 text-base">
        <UserPlus className="size-4 text-muted-foreground" />
        Without settings
      </CardTitle>
    </CardHeader>
    <CardContent>
      {!accountsAvailable ? (
        <p className="text-sm text-muted-foreground">The market maker account list could not be loaded from QuestDB. Settings above are unaffected.</p>
      ) : accounts.length ? (
        <UnconfiguredAccountList accounts={accounts} onSetUp={canEdit ? onSetUp : undefined} pendingPid={pendingPid} />
      ) : (
        <p className="text-sm text-muted-foreground">Every market maker account has settings.</p>
      )}
    </CardContent>
  </Card>
);
UnconfiguredPanel.displayName = 'UnconfiguredPanel';
